import { Component, inject, Input, OnDestroy, OnInit, input, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { VideoResponseDto } from '../../common/general-types/videos.dto';
import { CreatorResponseDto } from '../../common/general-types/creators.dto';
import { SubmittedPostDto, SubmittedPostStatusEnum, SubmittedPostVersionEnum } from '../../common/general-types/submitted-posts.dto';
import { SocialChannelEnum } from '../../common/general-types/briefs.dto';
import { SubmittedPostsService } from '../../services/submitted-posts/submitted-posts.service';
import { SuperSocialService } from '../../services/super-social/super-social.service';
import { FormatService } from '../../common/format/format.service';
import { Subscription, take } from 'rxjs';
import { ToastNotificationService } from '../toast-notification/toast-notification.service';
import { HasRoleDirectiveDirective } from "@/app/common/directives/has-role/has-role-directive.directive";
import { AlertService } from '../alert/alert.service';
import { EditSubmittedPost } from './atoms/edit-submitted-post/edit-submitted-post';
import { SubmittedPostFields } from './atoms/submitted-post-fields/submitted-post-fields';
import { UltraSocialService } from '@/app/services/ultra-social/ultra-social';
import { LatestPostResultsDto } from '../../common/general-types/super-social.dto';
import {
  compareCta,
  compareHashtags,
  displayHashtag,
  unwrapLatestResults,
  type CtaComparison,
  type HashtagComparison,
  type PostResultStats,
} from './individual-post-results.helpers';

type SuperSocialPostStats = Partial<LatestPostResultsDto> & PostResultStats;

@Component({
  selector: 'atom-individual-post-results',
  standalone: true,
  imports: [HasRoleDirectiveDirective, EditSubmittedPost, SubmittedPostFields, NgTemplateOutlet],
  templateUrl: './individual-post-results.component.html',
  styleUrl: './individual-post-results.component.css'
})
export class IndividualPostResultsComponent implements OnInit, OnDestroy {

  @Input('applicationId') applicationId: string | undefined;
  @Input('video') video: VideoResponseDto | undefined;
  @Input('creator') creator: CreatorResponseDto | undefined;
  @Input('primarySocialChannel') primarySocialChannel: SocialChannelEnum | number | undefined;
  @Input('briefCrossPlatforms') briefCrossPlatforms: (string | number)[] = [];
  /** Brief-prescribed hashtags (`shoutBrief.suggestedHastags`) for mismatch comparison. */
  readonly suggestedHashtags = input<string | undefined>(undefined);
  /** Brief-prescribed CTA for Wrong CTA Link comparison. */
  readonly callToActionLink = input<string | undefined>(undefined);

  selectedChannelTab = signal<number>(-1);

  crossPlatforms: number[] = [];
  primaryChannelTab: number | undefined;
  showChannelTabs = false;

  SubmittedPostStatusEnum = SubmittedPostStatusEnum;
  SubmittedPostVersionEnum = SubmittedPostVersionEnum;
  SocialChannelEnum = SocialChannelEnum;

  allSubmittedPosts = signal<SubmittedPostDto[]>([]);
  focussedSubmittedPost = signal<SubmittedPostDto | undefined>(undefined);

  superSocialResults = signal<Record<string, SuperSocialPostStats> | undefined>(undefined);
  resultsLoading = signal(false);

  noSubmittedPosts = signal<boolean>(true);
  hasPostsNoResults = signal<boolean>(false);

  validationInProgress = signal<boolean>(false);

  editSubmittedPost = signal<boolean>(false);


  socialChannelOptions: {
    channelName: string;
    shortName: string;
    channelId: number;
    channelIcon: string;
  }[] = [
      {
        channelName: 'All Channels',
        shortName: 'All',
        channelId: -1,
        channelIcon: '../../../../../assets/icon/tik-tok-transparent.svg',
      },
      {
        channelName: 'TikTok Post',
        shortName: 'Post',
        channelId: 0,
        channelIcon: '../../../../../assets/icon/tik-tok-transparent.svg',
      },
      {
        channelName: 'Instagram Reel',
        shortName: 'Reel',
        channelId: 1,
        channelIcon: '../../../../../assets/icon/ig_reels_logo.png',
      },
      {
        channelName: 'Instagram Story',
        shortName: 'Story',
        channelId: 2,
        channelIcon: '../../../../../assets/icon/instagram-logo.png',
      },
      {
        channelName: 'YouTube Shorts',
        shortName: 'Short',
        channelId: 3,
        channelIcon: '../../../../../assets/icon/youtube_shorts.webp',
      },
      {
        channelName: 'Snapchat Spotlight',
        shortName: 'Spotlight',
        channelId: 4,
        channelIcon: '../../../../../assets/icon/snapchat-logo.svg',
      },
      {
        channelName: 'Snapchat Story',
        shortName: 'Story',
        channelId: 5,
        channelIcon: '../../../../../assets/icon/snapchat_story.jpg',
      },
    ];

  /** Services */
  private submittedPostsService = inject(SubmittedPostsService);
  private superSocialService = inject(SuperSocialService);
  private ultraSocialService = inject(UltraSocialService);
  public formatService = inject(FormatService);
  public toastNotificationService = inject(ToastNotificationService);
  private alertService = inject(AlertService);
  private subscriptionList = new Subscription();
  /** Services - END */

  ngOnInit(): void {
    console.log('🚨 IndividualPost - Results -> ngOnInit');
    this.initialisePostResults();
  }

  initialisePostResults() {
    if (!this.applicationId || !this.video) {
      this.noSubmittedPosts.set(true);
      return;
    }

    this.submittedPostsService.getSubmittedPostsForBriefApplication(this.applicationId).pipe(take(1)).subscribe((submittedPostsRes: SubmittedPostDto[]) => {
      console.log('🚨 IndividualPostResults -> initialisePostResults -> submittedPostsRes: ', submittedPostsRes);
      const videoPosts = this.getPostsForVideo(submittedPostsRes ?? [], this.video!.id);

      if (videoPosts.length === 0) {
        this.noSubmittedPosts.set(true);
        this.crossPlatforms = [];
        this.primaryChannelTab = undefined;
        this.showChannelTabs = false;
        this.allSubmittedPosts.set([]);
        this.focussedSubmittedPost.set(undefined);
        return;
      }

      this.noSubmittedPosts.set(false);
      this.allSubmittedPosts.set(videoPosts);
      const normalizedCross = this.normalizeCrossPlatforms(this.briefCrossPlatforms);
      this.crossPlatforms = this.buildCrossPlatformTabs(videoPosts, normalizedCross);
      this.primaryChannelTab = this.buildPrimaryChannelTab(normalizedCross);
      this.showChannelTabs = this.primaryChannelTab !== undefined && normalizedCross.length > 0;
      this.focusPost(this.resolveInitialChannel(videoPosts));
    });
  }

  private normalizeCrossPlatforms(raw: (string | number)[] | undefined): number[] {
    if (!raw?.length) {
      return [];
    }

    return raw
      .map((plt) => Number(plt))
      .filter((plt) => !Number.isNaN(plt));
  }

  private getPostsForVideo(posts: SubmittedPostDto[], videoId: string): SubmittedPostDto[] {
    return posts.filter((post) => post.videoId === videoId);
  }

  private findPostForVideoAndChannel(
    posts: SubmittedPostDto[],
    videoId: string,
    channel: number,
  ): SubmittedPostDto | undefined {
    return posts.find((post) => post.videoId === videoId && post.socialChannel === channel);
  }

  private buildPrimaryChannelTab(briefCrossPlatforms: number[]): number | undefined {
    if (!briefCrossPlatforms.length) {
      return undefined;
    }

    const primary = Number(this.primarySocialChannel);
    if (Number.isNaN(primary) || primary < 0) {
      return undefined;
    }

    return primary;
  }

  private buildCrossPlatformTabs(
    videoPosts: SubmittedPostDto[],
    briefCrossPlatforms: number[],
  ): number[] {
    if (!briefCrossPlatforms.length) {
      return [];
    }

    const primary = this.primarySocialChannel !== undefined ? Number(this.primarySocialChannel) : undefined;
    const postedCrossChannels = new Set(
      videoPosts
        .map((post) => post.socialChannel)
        .filter((channel) => briefCrossPlatforms.includes(channel))
        .filter((channel) => primary === undefined || channel !== primary),
    );

    return briefCrossPlatforms.filter((channel) => postedCrossChannels.has(channel));
  }

  private resolveInitialChannel(videoPosts: SubmittedPostDto[]): number {
    const primary = this.primarySocialChannel !== undefined ? Number(this.primarySocialChannel) : undefined;

    if (primary !== undefined && !Number.isNaN(primary)) {
      return primary;
    }

    return videoPosts[0]?.socialChannel ?? -1;
  }

  private focusPost(channel: number): void {
    this.selectedChannelTab.set(channel);

    if (!this.video || channel === -1) {
      this.focussedSubmittedPost.set(undefined);
      return;
    }

    const post = this.findPostForVideoAndChannel(this.allSubmittedPosts(), this.video.id, channel);
    this.focussedSubmittedPost.set(post);

    console.log('🚨 IndividualPostResults -> focusPost -> post: ', post);

    this.loadSuperSocialResultsForPost(post);
  }

  private loadSuperSocialResultsForPost(post: SubmittedPostDto | undefined): void {
    if (post?.resultsVersion === SubmittedPostVersionEnum.SuperSocial) {
      const lookupIds = [post.ss_post_id, post.fullUrl, post.videoLink].filter(
        (id): id is string => typeof id === 'string' && id.length > 0,
      );
      if (lookupIds.length) {
        this.getSuperSocialResults(lookupIds);
      }
    } else if (post?.resultsVersion === SubmittedPostVersionEnum.UltraSocial) {
      /**
       * When the results are UltraSocial && TikTok
       * We need to get these results from the TikTok One API
       */
      const ssPlatformPostId = this.extractSuperSocialIds(post);
      if (ssPlatformPostId) {
        this.getUltraSocialResults(ssPlatformPostId);
      }
    }
  }

  onSubmittedPostSaved(submittedPost: SubmittedPostDto) {
    console.log('🚨 IndividualPostResults -> onSubmittedPostSaved -> submittedPost: ', submittedPost);
    this.noSubmittedPosts.set(true);
    this.allSubmittedPosts.set([]);
    this.focussedSubmittedPost.set(undefined);
    this.editSubmittedPost.set(false);
    this.initialisePostResults();
  }

  getSuperSocialResults(postIds: string[]) {
    console.log('-->> submittedPostIds: ', postIds);
    this.resultsLoading.set(true);
    this.superSocialResults.set(undefined);
    this.hasPostsNoResults.set(false);

    this.subscriptionList.add(
      this.superSocialService.getLatestResultsForPosts(postIds).pipe(take(1)).subscribe({
        next: superSocialResultsRes => {
          this.superSocialResults.set(unwrapLatestResults(superSocialResultsRes));
          this.resultsLoading.set(false);
          console.log('🤢🤢🤢 superSocialResultsRes: ', superSocialResultsRes);
        },
        error: error => {
          console.error('superSocialResultsRes Error: ', error);
          this.superSocialResults.set({});
          this.resultsLoading.set(false);
          this.hasPostsNoResults.set(true);
        }
      }),
    );
  }

  private findSuperSocialResult(post: SubmittedPostDto): SuperSocialPostStats | undefined {
    const results = this.superSocialResults();
    if (!results) {
      return undefined;
    }

    const keys = [post.ss_post_id, post.fullUrl, post.videoLink].filter(
      (id): id is string => typeof id === 'string' && id.length > 0,
    );
    for (const key of keys) {
      if (results[key]) {
        return results[key];
      }
    }

    const values = Object.values(results);
    return values.length === 1 ? values[0] : undefined;
  }

  extractSuperSocialIds(item: SubmittedPostDto): string {
    if (item.ss_post_id && typeof item.ss_post_id === 'string') {
      return item.ss_post_id;
    }
    console.warn('Object without valid ID found:', item);
    return '';
  }

  getUltraSocialResults(submittedPostId: string) {
    console.log('-->> ULTRA submittedPostId: ', submittedPostId);
    this.resultsLoading.set(true);
    this.superSocialResults.set(undefined);
    this.hasPostsNoResults.set(false);

    this.subscriptionList.add(
      this.ultraSocialService.getCompleteLatestResultsForPosts([submittedPostId]).pipe(take(1)).subscribe({
        next: ultraSocialResultsRes => {
          this.superSocialResults.set(unwrapLatestResults(ultraSocialResultsRes));
          this.resultsLoading.set(false);
          console.log('🤢🤢🤢 ultraSocialResultsRes: ', ultraSocialResultsRes);
        },
        error: error => {
          console.error('ultraSocialResultsRes Error: ', error);
          this.superSocialResults.set({});
          this.resultsLoading.set(false);
          this.hasPostsNoResults.set(true);
        }
      }),
    );
  }

  private formatCount(value: unknown): string {
    if (this.resultsLoading()) {
      return '—';
    }
    return this.formatService.convertIntToString(value ?? 0);
  }

  private hasImplausibleViewCount(result: SuperSocialPostStats | undefined): boolean {
    if (!result) {
      return false;
    }
    const views = Number(result.views) || 0;
    const likes = Number(result.likes) || 0;
    const comments = Number(result.comments) || 0;
    return views > 0 && (views < likes || views < comments);
  }

  getViews(post: SubmittedPostDto) {
    const result = this.findSuperSocialResult(post);
    if (this.resultsLoading() || this.hasImplausibleViewCount(result)) {
      return '—';
    }
    return this.formatCount(result?.views);
  }

  getLikes(post: SubmittedPostDto) {
    return this.formatCount(this.findSuperSocialResult(post)?.likes);
  }

  getComments(post: SubmittedPostDto) {
    return this.formatCount(this.findSuperSocialResult(post)?.comments);
  }

  getShares(post: SubmittedPostDto) {
    return this.formatCount(this.findSuperSocialResult(post)?.shares);
  }

  getEngagements(post: SubmittedPostDto) {
    if (this.resultsLoading() || this.hasImplausibleViewCount(this.findSuperSocialResult(post))) {
      return '—';
    }
    const result = this.findSuperSocialResult(post);
    return result ? `${result.engagementRate}%` : '0%';
  }

  getEngagementBarWidth(post: SubmittedPostDto) {
    if (this.resultsLoading()) {
      return '0%';
    }
    return this.getEngagements(post);
  }

  getCaption(post: SubmittedPostDto) {
    if (this.resultsLoading()) {
      return '—';
    }
    const result = this.findSuperSocialResult(post);
    return result?.caption ? `${result.caption}` : 'No caption found';
  }

  hasPostResults(): boolean {
    const post = this.focussedSubmittedPost();
    return !!post && !!this.findSuperSocialResult(post);
  }

  hashtagComparison(): HashtagComparison | null {
    const post = this.focussedSubmittedPost();
    if (!post || post.status !== SubmittedPostStatusEnum.MismatchedHashtag) {
      return null;
    }
    const result = this.findSuperSocialResult(post);
    return compareHashtags(this.suggestedHashtags(), result?.hashtags, result?.caption);
  }

  ctaComparison(): CtaComparison | null {
    const post = this.focussedSubmittedPost();
    if (!post || post.status !== SubmittedPostStatusEnum.WrongCtaLink) {
      return null;
    }
    const result = this.findSuperSocialResult(post);
    return compareCta(this.callToActionLink(), result?.caption);
  }

  formatHashtag(tag: string): string {
    return displayHashtag(tag);
  }

  async validatePost() {
      console.log('🚨 IndividualPostResults -> validatePost');
      const confirm = await this.alertService.confirm({
        title: 'Validate Post?',
        message: 'Are you sure you want to re-validate the post?',
        confirmText: 'Validate',
        cancelText: 'Cancel',
        type: 'confirm',
        icon: 'warning',
      });
      console.log('🚨 IndividualPostResults -> validatePost -> confirm: ', confirm);

      if (confirm) {
        this.validationInProgress.set(true);
        this.submittedPostsService.revalidateLink(this.focussedSubmittedPost()!.id).subscribe({
          next: res => {
            console.log('🚨 IndividualPostResults -> validatePost -> res: ', res);
            this.validationInProgress.set(false);
            this.toastNotificationService.alert({
              title: 'Re-Validation Submitted!',
              message: 'Pleae check back later for the results.',
              type: 'success',
              icon: 'success'
            });
          },
          error: err => {
            console.error('🚨 IndividualPostResults -> validatePost -> err: ', err);
            this.validationInProgress.set(false);
            this.toastNotificationService.alert({
              title: 'Error Re-Validating Post!',
              message: 'Something went wrong. If the problem persists, please contact support.',
              type: 'error',
              icon: 'error'
            });
          }
        });
      }
    }

  async forceValidate() {
      console.log('🚨 IndividualPostResults -> forceValidate');
      const confirm = await this.alertService.confirm({
        title: 'Force Validate Post?',
        message: 'Are you sure you want to force this one through?',
        confirmText: 'Force',
        cancelText: 'Cancel',
        type: 'confirm',
        icon: 'error',
      });
      console.log('🚨 IndividualPostResults -> forceValidate -> confirm: ', confirm);

      if (confirm) {
        this.validationInProgress.set(true);
        this.validationInProgress.set(true);
        this.submittedPostsService.pushThrough(this.applicationId!).subscribe({
          next: res => {
            console.log('🚨 IndividualPostResults -> validatePost -> res: ', res);
            this.validationInProgress.set(false);
            this.toastNotificationService.alert({
              title: 'Re-Validation Submitted!',
              message: 'Pleae check back later for the results.',
              type: 'success',
              icon: 'success'
            });
          },
          error: err => {
            console.error('🚨 IndividualPostResults -> validatePost -> err: ', err);
            this.validationInProgress.set(false);
            this.toastNotificationService.alert({
              title: 'Error Re-Validating Post!',
              message: 'Something went wrong. If the problem persists, please contact support.',
              type: 'error',
              icon: 'error'
            });
          }
        });
      }
    }

    editPost() {
      console.log('🚨 IndividualPostResults -> editPost');
      this.editSubmittedPost.set(true);
    }

    updateSelectedChannel(channel: number) {
      this.focusPost(channel);
    }

    getPlatformTabClass(channel: number) {
      return this.selectedChannelTab() === channel
        ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
        : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
    }

    getStatusName() {
      if (this.focussedSubmittedPost()) {
        switch (this.focussedSubmittedPost()!.status) {
          case SubmittedPostStatusEnum.Created:
            return 'Post Created';
          case SubmittedPostStatusEnum.Updated:
            return 'Post Updated';
          case SubmittedPostStatusEnum.Processing:
            return 'Results Processing';
          case SubmittedPostStatusEnum.Valid:
            return 'Valid Results & Post';
          case SubmittedPostStatusEnum.Invalid:
            return 'Invalid Post';
          case SubmittedPostStatusEnum.NotFound:
            return 'Post Not Found';
          case SubmittedPostStatusEnum.WrongAccount:
            return 'Posted on the wrong account';
          case SubmittedPostStatusEnum.Duplicate:
            return 'Duplicate Post';
          case SubmittedPostStatusEnum.Deleted:
            return 'Post Deleted';
          case SubmittedPostStatusEnum.AccountNotConnected:
            return 'Account Not Connected';
          case SubmittedPostStatusEnum.ManualCollection:
            return 'Manual Collection Required';
          case SubmittedPostStatusEnum.MismatchedHashtag:
            return 'Mismatched Hashtag Discovered';
          case SubmittedPostStatusEnum.WrongCtaLink:
            return 'Wrong CTA Link Discovered';
          default:
            return 'Unknown';
        }
      }
      return 'Loading...';
    }


    getStatusDescription() {
      if (this.focussedSubmittedPost()) {
        switch (this.focussedSubmittedPost()!.status) {
          case SubmittedPostStatusEnum.Created:
            return 'The post has been created and is waiting for results.';
          case SubmittedPostStatusEnum.Updated:
            return 'The post has been updated and is waiting for results.';
          case SubmittedPostStatusEnum.Processing:
            return 'The results are being processed. This may take some time to load.';
          case SubmittedPostStatusEnum.Valid:
            return 'The results are valid and the post is live.';
          case SubmittedPostStatusEnum.Invalid:
            return 'The post is live, but there is a problem with the post itself.';
          case SubmittedPostStatusEnum.NotFound:
            return 'The post is not found. Please check the post ID and try again.';
          case SubmittedPostStatusEnum.WrongAccount:
            return 'The post is posted on the wrong account. Please check the account and try again.';
          case SubmittedPostStatusEnum.Duplicate:
            return 'The post is a duplicate. Please check the post and try again.';
          case SubmittedPostStatusEnum.Deleted:
            return 'The post is deleted. Please check the post and try again.';
          case SubmittedPostStatusEnum.AccountNotConnected:
            return 'The account is not connected. Please connect the account and try again.';
          case SubmittedPostStatusEnum.ManualCollection:
            return 'Manual collection is required. Please collect the post manually and try again.';
          case SubmittedPostStatusEnum.MismatchedHashtag:
            return 'The hashtags on the post do not match the brief requirements. Please check the hashtag and try again.';
          case SubmittedPostStatusEnum.WrongCtaLink:
            return 'The CTA link on the post does not match the brief requirements. Please check the CTA link and try again.';
          default:
            return 'The post status is unknown. Please check the post and try again.';
        }
      }
      return 'Loading...';
    }

    getChannel(channel: any) {
      const found = this.socialChannelOptions.find(chan => chan.channelId === channel);
      return found
    }

    ngOnDestroy(): void {
      this.subscriptionList.unsubscribe();
    }
  }
